import type { Game } from "@/types/game";
import { GameSize } from "@/types/game";
import type { GameWithRelations } from "@/types/game";

// Shared types for game data transformation

export type PublisherData = {
  id: number;
  name: string;
  slug: string;
  created_at: string;
  updated_at: string;
  logo_url: string | null;
  website_url: string | null;
};

export type GameTagJunction = {
  tag: {
    id: number;
    name: string;
    slug: string;
    description: string | null;
    display_order: number | null;
    created_at: string;
    updated_at: string;
  };
};

export type GamePublisherJunction = {
  publisher: PublisherData;
};

export type RawGameData = Game & {
  tags?: GameTagJunction[];
  publisher?: PublisherData | null;
  publishers?: GamePublisherJunction[];
};

// Shared transformation function for game data with relationships
export function transformGameData(data: RawGameData[]): GameWithRelations[] {
  return (
    data?.map((game) => ({
      ...game,
      tags: game.tags?.map((t: GameTagJunction) => t.tag) || [],
      publisher: game.publisher || null,
      // Handle multiple publishers if present
      publishers: game.publishers?.map((p: GamePublisherJunction) => p.publisher) || [],
      publisher_ids: game.publishers?.map((p: GamePublisherJunction) => p.publisher.id) || [],
    })) || []
  );
}

export function normalizeGames(fetched: Partial<Game>[] = []): Game[] {
  return fetched.map((game) => ({
    ...game,
    size: (game.size ?? GameSize.Small) as GameSize,
  })) as Game[];
}

export function transformGameForDisplay(game: GameWithRelations) {
  return {
    ...game,
    tags: game.tags ?? [],
    publisher: game.publisher ?? null,
  };
}
// Types for row-based organization
export interface GameRow {
  tag: {
    id: number;
    name: string;
    /** Optional; legacy content-order rows may still expose a slug. */
    slug?: string;
    description: string | null;
    display_order: number | null;
  };
  games: GameWithRelations[];
}
